"use client";

import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/card";

interface FormData {
  budget: number;
  location: "sobre suelo" | "en tejado" | "";
  orientation: string[];
  area: number;
}

interface TableData {
  id: number;
  item: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export default function BudgetCalculator() {
  const [formData, setFormData] = useState<FormData>({
    budget: 50000,
    location: "",
    orientation: [],
    area: 0,
  });
  
  const [showProgress, setShowProgress] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [showTable, setShowTable] = useState(false);
  const [tableData, setTableData] = useState<TableData[]>([]);

  // Simular el cálculo de la tabla con datos realistas
  const calculateTableData = (): TableData[] => {
    const solarPanels = Math.floor(formData.budget / 500); // Aproximadamente 500€ por panel
    const inverterCost = Math.floor(solarPanels * 0.3 * 500); // Inversor ~30% del costo de paneles
    const installationCost = Math.floor(formData.budget * 0.15); // 15% del presupuesto para instalación
    const otherCosts = Math.floor(formData.budget * 0.1); // 10% para otros costos
    
    return [
      {
        id: 1,
        item: "Paneles Solares",
        description: `Sistema de ${solarPanels} paneles solares`,
        quantity: solarPanels,
        unitPrice: 500,
        total: solarPanels * 500,
      },
      {
        id: 2,
        item: "Inversor",
        description: "Inversor de alta eficiencia",
        quantity: 1,
        unitPrice: inverterCost,
        total: inverterCost,
      },
      {
        id: 3,
        item: "Instalación",
        description: "Servicio profesional de instalación",
        quantity: 1,
        unitPrice: installationCost,
        total: installationCost,
      },
      {
        id: 4,
        item: "Otros componentes",
        description: "Cables, estructura, protecciones",
        quantity: 1,
        unitPrice: otherCosts,
        total: otherCosts,
      },
    ];
  };

  const handleCalculate = () => {
    setShowProgress(true);
    setProgressValue(0);
    setShowTable(false);
    
    // Simular progreso
    const interval = setInterval(() => {
      setProgressValue((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 220); // 220ms * 10 pasos = 2200ms (2.2 segundos)
    
    // Al finalizar el progreso, mostrar la tabla
    setTimeout(() => {
      clearInterval(interval);
      setProgressValue(100);
      const calculatedData = calculateTableData();
      setTableData(calculatedData);
      setShowProgress(false);
      setShowTable(true);
    }, 2200);
  };

  const handleOrientationClick = (direction: string) => {
    setFormData(prev => {
      const current = [...prev.orientation];
      const index = current.indexOf(direction);
      
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(direction);
      }
      
      return {
        ...prev,
        orientation: current
      };
    });
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h2 className="text-xl font-bold mb-2">Calcula el ROI de tu instalación</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Retorno de la Inversión, basado en la estimación de tu generación solar</p>
      </div>

      <div className="mb-6">
        <hr className="border-t border-gray-200 dark:border-gray-700" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative mb-6">
        <div className="md:col-span-1 md:row-span-2 relative">
          <div className="absolute inset-y-0 right-0 w-px bg-gray-200 dark:bg-gray-700 opacity-50 hidden md:block"></div>
          <div className="mb-12 md:pr-4">
            <h2 className="text-xl font-bold mb-4">1 - Elige el lugar de tu instalación</h2>
            <div className="flex space-x-4">
              <Button
                variant={formData.location === "sobre suelo" ? "default" : "outline"}
                onClick={() => setFormData({...formData, location: "sobre suelo"})}
                className="flex-1"
              >
                Sobre suelo
              </Button>
              <Button
                variant={formData.location === "en tejado" ? "default" : "outline"}
                onClick={() => setFormData({...formData, location: "en tejado"})}
                className="flex-1"
              >
                En tejado
              </Button>
            </div>
          </div>

          <div className="mb-12 md:pr-4">
            <h2 className="text-xl font-bold mb-4">2 - La orientación de los paneles</h2>
            <div className="flex flex-col items-center">
              <div className="grid grid-cols-3 gap-2">
                <div></div>
                <Button
                  variant={formData.orientation.includes("Norte") ? "default" : "outline"}
                  onClick={() => handleOrientationClick("Norte")}
                  className="w-16 h-16 flex flex-col items-center justify-center"
                >
                  <span>↑</span>
                  <span className="text-xs mt-1">Norte</span>
                </Button>
                <div></div>

                <Button
                  variant={formData.orientation.includes("Oeste") ? "default" : "outline"}
                  onClick={() => handleOrientationClick("Oeste")}
                  className="w-16 h-16 flex items-center justify-center"
                >
                  <span>←</span>
                  <span>Oeste</span>
                </Button>
                <div className="w-16 h-16 flex items-center justify-center">
                </div>
                <Button
                  variant={formData.orientation.includes("Este") ? "default" : "outline"}
                  onClick={() => handleOrientationClick("Este")}
                  className="w-16 h-16 flex items-center justify-center"
                >
                  <span>Este</span>
                  <span>→</span>
                </Button>

                <div></div>
                <Button
                  variant={formData.orientation.includes("Sur") ? "default" : "outline"}
                  onClick={() => handleOrientationClick("Sur")}
                  className="w-16 h-16 flex flex-col items-center justify-center"
                >
                  <span className="text-xs mb-1">Sur</span>
                  <span>↓</span>
                </Button>
                <div></div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-12 md:pl-4">
          <h2 className="text-xl font-bold mb-4">3 - El área disponible</h2>
          <div className="relative">
            <input
              type="text"
              value={formData.area.toLocaleString("es-ES")}
              onChange={(e) => {
                const value = e.target.value.replace(/\./g, '').replace(/[^\d]/g, '');
                setFormData({...formData, area: value ? parseInt(value) : 0});
              }}
              className="w-full p-2 border rounded-md pr-16 text-right"
              placeholder="0"
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">m²</span>
          </div>
        </div>

        <div className="mb-12 md:pl-4">
          <h2 className="text-xl font-bold mb-4">4 - Tu presupuesto</h2>
          <div className="px-4">
            <Slider
              value={[formData.budget]}
              onValueChange={(value) => setFormData({...formData, budget: value[0]})}
              max={60000}
              min={5000}
              step={1000}
              className="w-full"
            />
            <div className="flex justify-between mt-2">
              <span>5.000€</span>
              <span className="font-bold">{formData.budget.toLocaleString("es-ES")}€</span>
              <span>60.000€+</span>
            </div>
          </div>
        </div>
        
        <div className="col-span-2 flex justify-center mb-6 mt-8">
          <Button 
            onClick={handleCalculate}
            className="px-8 py-6 text-lg font-bold"
          >
            CALCULAR
          </Button>
        </div>
        
        {showProgress && (
          <div className="col-span-2 mb-6">
            <h3 className="text-lg font-semibold mb-2">Calculando...</h3>
            <Progress value={progressValue} className="w-full" />
          </div>
        )}
        
        {showTable && (
          <div className="col-span-2 mt-6">
            <h3 className="text-lg font-semibold mb-4">Presupuesto detallado</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="text-left p-3">Item</th>
                    <th className="text-left p-3">Descripción</th>
                    <th className="text-right p-3">Cant.</th>
                    <th className="text-right p-3">Precio Unit.</th>
                    <th className="text-right p-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row) => (
                    <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="p-3 font-medium">{row.item}</td>
                      <td className="p-3 text-gray-600 dark:text-gray-300">{row.description}</td>
                      <td className="p-3 text-right">{row.quantity}</td>
                      <td className="p-3 text-right">{row.unitPrice.toLocaleString("es-ES")}€</td>
                      <td className="p-3 text-right font-semibold">{row.total.toLocaleString("es-ES")}€</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                    <td className="p-3"></td>
                    <td className="p-3"></td>
                    <td className="p-3"></td>
                    <td className="p-3 text-right pr-3">TOTAL:</td>
                    <td className="p-3 text-right">
                      {tableData.reduce((sum, row) => sum + row.total, 0).toLocaleString("es-ES")}€
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}